'use client';
import React, { useState } from 'react';
import { Locale, LocaleDetails } from 'shared/src/types/languages.type';
import { deleteArticle } from '@/server/articles/delete-article';
import { IoTrash, IoClose } from 'react-icons/io5';
import { articleAdminUIContent } from '@/lib/ui-content/article-ui-content';

type DeleteArticleModalProps = {
  lang: Locale;
  locale: LocaleDetails;
  articleId: string;
  articleTitle: string;
  onError: (error: string) => void;
  onCancel: () => void;
};

export const DeleteArticleModal = ({ 
  lang, 
  locale, 
  articleId, 
  articleTitle, 
  onError, 
  onCancel 
}: DeleteArticleModalProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const uiContent = articleAdminUIContent[locale.languageCode] || articleAdminUIContent.en;

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const result = await deleteArticle({ lang, id: articleId });
      if (!result.success && result.error) {
        onError(result.error);
        onCancel();
      }
      // If successful, the function will redirect automatically
    } catch (error) {
      console.error('Error deleting article:', error);
      onError(error instanceof Error ? error.message : 'Unknown error occurred');
      onCancel();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-md animate-fadeIn rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-red-100 p-2 dark:bg-red-900">
              <IoTrash className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {uiContent.deleteArticle}
            </h2>
          </div>
          <button
            onClick={onCancel}
            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
            disabled={isDeleting}
          >
            <IoClose className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-6">
          <p className="mb-4 text-gray-600 dark:text-gray-300">
            {uiContent.confirmDeleteArticle}
          </p>
          
          <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Article: &ldquo;{articleTitle}&rdquo;
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm text-white transition-colors hover:bg-red-700 disabled:opacity-50"
          >
            {isDeleting ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Deleting...
              </>
            ) : (
              <>
                <IoTrash className="h-4 w-4" />
                {uiContent.deleteArticle}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
